import type { CallbackAction, GeolocationCallback } from '@fanduel/geolocation-sdk';
import type { Session } from '@fanduel/cpe-um-bundle/account/contract';
import type { Experiment } from '@/types/aggregation/home';
import { AMPLITUDE_EXPERIMENTS, isExperimentEnabled } from '@/utils/experiments';
import type { GeolocationConfig } from '@/types/config';
import { getConfig } from '@/utils/config/config';
import { COOKIES, setCookie } from '@/utils/cookies';
import { getCookieValue } from '@/utils/cookies/client';
import {
    beginLoginGeolocation,
    endLoginGeolocation,
    resetLoginGeolocationGate
} from '@/logic/geolocation/login-geolocation-gate';
import { clearLoginGeoPill, setLoginGeoPillTransientResult } from '@/logic/geolocation/login-geo-pill-state';
import buildProviderConfig from '@/logic/geolocation/geolocation-config';

import { loginGeoRum } from '@/logic/geolocation/login-geo-rum';
import { GeoLogRumTypes, LocationVerificationPillVisual } from '@/logic/geolocation/login-geo-types.types';
import { requestGeolocationPermissionsOnPageLoad } from '@/logic/geolocation/geolocation-permission-precheck';
import { dispatchGeolocationResultToEmbeddedLauncher } from '@/logic/web-wrapper/dispatch-geolocation-result-to-embedded-launcher';
import type { Region } from '@/types/constants';
import { jwtDecode } from 'jwt-decode';

type GeolocationSdkModule = Awaited<ReturnType<typeof loadGeolocationSdk>>;

export type LoginGeolocationInput = {
    experiments: Experiment;
    isMobileWebLobby: boolean;
    isDesktop: boolean;
    session: Pick<Session, 'sessionId' | 'token'>;
    userId: string;
    region: Region;
};

export type LobbyLocateUserInput = {
    session: Pick<Session, 'sessionId' | 'token'>;
    userId: string;
    region: Region;
    /** Geocomply product domain (e.g. casino, poker). Defaults to process.env.PRODUCT. */
    product?: string;
    /** Passed to Geolocation.locateUser. Defaults to "Login". */
    reason?: string;
};

const LOGIN_LOCATE_REASON = 'Login';
export const WEB_WRAPPER_LOCATE_REASON = 'web wrapper';
const loadGeolocationSdk = () => import('@fanduel/geolocation-sdk');

let geolocationSdkPromise: Promise<GeolocationSdkModule> | null = null;
let lobbyGeoSdkInitialized = false;
let lobbyGeoConfigKey: string | null = null;

let lobbyLocateInFlight: Promise<void> | null = null;
let lobbyLocateInFlightKey: string | null = null;
let finishLobbyLocate: (() => void) | null = null;
let lastGeoResult: CallbackAction | null = null;

const buildLobbyGeoConfigKey = (input: LobbyLocateUserInput, product: string): string =>
    `${input.session.sessionId}|${input.userId}|${input.region}|${product}`;

const clearLobbyLocateInFlight = (): void => {
    lobbyLocateInFlight = null;
    lobbyLocateInFlightKey = null;
    finishLobbyLocate = null;
};

const isTerminalGeoAction = (sdk: GeolocationSdkModule, type: string): boolean => {
    const { GEOLOCATION_INVALIDATED, GEOLOCATION_PASSED, GEOLOCATION_REJECTED, LOCATION_FAILURE } = sdk;
    return (
        type === GEOLOCATION_PASSED ||
        type === GEOLOCATION_REJECTED ||
        type === LOCATION_FAILURE ||
        type === GEOLOCATION_INVALIDATED
    );
};

export const getGeolocationSdk = (): Promise<GeolocationSdkModule> => {
    if (!geolocationSdkPromise) {
        geolocationSdkPromise = loadGeolocationSdk();
    }
    return geolocationSdkPromise;
};

/** Prefetch SDK module and ask for browser permissions once at page load. */
export const prefetchLobbyGeolocation = (): void => {
    void getGeolocationSdk();
    requestGeolocationPermissionsOnPageLoad();
};

let lastGeolocationSessionId: string | null = null;

const hasExistingGeocomplyToken = (): boolean => Boolean(getCookieValue(COOKIES.X_GEOLOCATION_TOKEN));

export const resetLoginGeolocationDedupe = (): void => {
    lastGeolocationSessionId = null;
    lobbyGeoSdkInitialized = false;
    lobbyGeoConfigKey = null;
    lastGeoResult = null;
    clearLobbyLocateInFlight();
    resetLoginGeolocationGate();
    clearLoginGeoPill();
};

const buildLoginGeolocationCallback = (sdk: GeolocationSdkModule, silent: boolean): GeolocationCallback => {
    const {
        GEOLOCATION_INVALIDATED,
        GEOLOCATION_PASSED,
        GEOLOCATION_REJECTED,
        LOCATION_FAILURE,
        LOCATION_IN_PROGRESS
    } = sdk;

    return (action: CallbackAction) => {
        switch (action.type) {
            case LOCATION_IN_PROGRESS:
                if (!silent) {
                    beginLoginGeolocation();
                }
                loginGeoRum(GeoLogRumTypes.PROGRESS, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });
                break;
            case GEOLOCATION_PASSED:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.SUCCESS);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.SUCCESS);
                }

                loginGeoRum(GeoLogRumTypes.PASSED, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });

                if (action.payload.jwt) {
                    const { expires }: { expires: string } = jwtDecode(action.payload.jwt);
                    const expiryDate = new Date(expires);

                    setCookie(COOKIES.X_GEOLOCATION_TOKEN, action.payload.jwt, expiryDate.valueOf() - Date.now());
                }
                break;
            case GEOLOCATION_REJECTED:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.FAILED);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
                }

                loginGeoRum(GeoLogRumTypes.REJECTED, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });
                break;
            case LOCATION_FAILURE:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.FAILED);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
                }
                loginGeoRum(GeoLogRumTypes.FAILED, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });
                break;
            case GEOLOCATION_INVALIDATED:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.FAILED);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
                }
                loginGeoRum(GeoLogRumTypes.INVALIDATED, { silent });

                break;
            default:
                break;
        }

        if (isTerminalGeoAction(sdk, action.type)) {
            lastGeoResult = action;
            finishLobbyLocate?.();
            clearLobbyLocateInFlight();
        }

        dispatchGeolocationResultToEmbeddedLauncher(action);
    };
};

/** Shared lobby geolocation locate (login prefetch and WebWrapper triggerLocateUser). */
export const runLobbyLocateUser = async (
    input: LobbyLocateUserInput,
    geo: GeolocationConfig = getConfig().geolocation
): Promise<void> => {
    const reason = input.reason ?? LOGIN_LOCATE_REASON;
    const silent = hasExistingGeocomplyToken();
    const product = input.product ?? process.env.PRODUCT;
    const configKey = buildLobbyGeoConfigKey(input, product);

    if (lobbyLocateInFlight && lobbyLocateInFlightKey === configKey) {
        await lobbyLocateInFlight;
        if (lastGeoResult) {
            dispatchGeolocationResultToEmbeddedLauncher(lastGeoResult);
        }
        return;
    }

    if (lobbyLocateInFlight) {
        await lobbyLocateInFlight;
    }

    lobbyLocateInFlightKey = configKey;
    lobbyLocateInFlight = new Promise<void>((resolve) => {
        finishLobbyLocate = resolve;
    });

    try {
        if (!silent) {
            beginLoginGeolocation();
        }

        const sdk = await getGeolocationSdk();
        const { Geolocation } = sdk;

        if (!lobbyGeoSdkInitialized) {
            Geolocation.init(geo.serviceName, process.env.BUILD_VERSION ?? 'unknown');
            loginGeoRum(GeoLogRumTypes.INIT, { region: input.region, product });

            Geolocation.setCallback(buildLoginGeolocationCallback(sdk, silent));
            loginGeoRum(GeoLogRumTypes.CALLBACK_SET, { region: input.region, product });

            lobbyGeoSdkInitialized = true;
        }

        if (lobbyGeoConfigKey !== configKey) {
            await Geolocation.setConfig(
                buildProviderConfig(geo, {
                    region: input.region,
                    sessionId: input.session.sessionId,
                    userId: input.userId,
                    userAuthToken: input.session.token,
                    product
                })
            );
            lobbyGeoConfigKey = configKey;
            loginGeoRum(GeoLogRumTypes.CONFIG_SET, { region: input.region, product });
        }

        Geolocation.locateUser(reason);
    } catch (e) {
        clearLobbyLocateInFlight();
        if (!silent) {
            endLoginGeolocation(LocationVerificationPillVisual.FAILED);
            setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
        }
        loginGeoRum(GeoLogRumTypes.FAILED, { region: input.region, product });
        throw e;
    }
};

const runWebLoginLocate = async (input: LoginGeolocationInput, geo: GeolocationConfig): Promise<void> => {
    await runLobbyLocateUser(
        {
            session: input.session,
            userId: input.userId,
            region: input.region,
            reason: LOGIN_LOCATE_REASON
        },
        geo
    );
};

export const maybeRunLoginGeolocation = (input: LoginGeolocationInput): void => {
    if (!input.isMobileWebLobby && !input.isDesktop) {
        return;
    }

    if (!isExperimentEnabled(input.experiments, AMPLITUDE_EXPERIMENTS.SAME_DOMAIN_LAUNCH)) {
        return;
    }

    if (!input.userId || !input.region.trim()) {
        return;
    }

    if (input.session.sessionId === lastGeolocationSessionId) {
        return;
    }

    lastGeolocationSessionId = input.session.sessionId;

    const geo = getConfig().geolocation;
    runWebLoginLocate(input, geo);
};
